import { useEffect, useRef, useState } from "react";
import { Download, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { sampleEkg } from "../lib/ekg";

const W = 1200, H = 630;

function paint(ctx, { date, score, correct, total, streak, name }) {
  ctx.fillStyle = "#0a0a0d";
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(34,197,94,0.12)";
  ctx.lineWidth = 1;
  for (let x = 0; x < W; x += 30) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
  for (let y = 0; y < H; y += 30) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

  ctx.strokeStyle = "#4ade80";
  ctx.lineWidth = 3;
  ctx.shadowColor = "#4ade80";
  ctx.shadowBlur = 16;
  ctx.beginPath();
  for (let i = 0; i < W; i += 2) {
    const y = 470 - sampleEkg((i / W) * 6, { pattern: "sinus", rate: 75 }) * 70;
    if (i === 0) ctx.moveTo(i, y); else ctx.lineTo(i, y);
  }
  ctx.stroke();
  ctx.shadowBlur = 0;

  ctx.strokeStyle = "rgba(34,197,94,0.5)";
  ctx.lineWidth = 2;
  ctx.strokeRect(24, 24, W - 48, H - 48);

  ctx.fillStyle = "#9ca3af";
  ctx.font = "500 20px 'JetBrains Mono', monospace";
  ctx.fillText("FIBULA GAMES  //  BY GSOFTWARE LLC", 70, 90);
  ctx.fillStyle = "#e5e7eb";
  ctx.font = "800 54px 'Outfit', sans-serif";
  ctx.fillText("DAILY CHALLENGE", 70, 160);
  ctx.fillStyle = "#9ca3af";
  ctx.font = "500 22px 'JetBrains Mono', monospace";
  ctx.fillText(`${date}  ·  ${name || "Clinician"}`, 70, 200);

  ctx.fillStyle = "#4ade80";
  ctx.shadowColor = "#4ade80";
  ctx.shadowBlur = 24;
  ctx.font = "800 150px 'Outfit', sans-serif";
  ctx.fillText(String(score), 70, 370);
  ctx.shadowBlur = 0;
  const scoreW = ctx.measureText(String(score)).width;
  ctx.fillStyle = "#9ca3af";
  ctx.font = "500 26px 'JetBrains Mono', monospace";
  ctx.fillText("POINTS", 70 + scoreW + 24, 370);

  ctx.textAlign = "right";
  ctx.fillStyle = "#e5e7eb";
  ctx.font = "700 44px 'Outfit', sans-serif";
  ctx.fillText(`${correct} / ${total}`, W - 70, 300);
  ctx.fillStyle = "#9ca3af";
  ctx.font = "500 18px 'JetBrains Mono', monospace";
  ctx.fillText("CORRECT", W - 70, 328);
  ctx.fillStyle = "#e5e7eb";
  ctx.font = "700 44px 'Outfit', sans-serif";
  ctx.fillText(`${streak} day${streak === 1 ? "" : "s"}`, W - 70, 390);
  ctx.fillStyle = "#9ca3af";
  ctx.font = "500 18px 'JetBrains Mono', monospace";
  ctx.fillText("DAILY STREAK", W - 70, 418);
  ctx.textAlign = "left";

  ctx.fillStyle = "#6b7280";
  ctx.font = "500 16px 'JetBrains Mono', monospace";
  ctx.fillText("For entertainment & study only — not for clinical decision-making.", 70, 570);
}

export function shareText({ date, score, correct, total, streak }) {
  const blocks = Array.from({ length: total }, (_, i) => (i < correct ? "█" : "░")).join("");
  return `Fibula Games Daily ${date}\n${blocks} ${correct}/${total}\n${score} pts · ${streak}-day streak\nGSoftware LLC`;
}

export function ScoreCard({ result, name }) {
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const data = { ...result, name };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const run = () => paint(ctx, data);
    run();
    document.fonts?.ready?.then(run);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.score, result.correct, result.total, result.streak, result.date, name]);

  const download = () => {
    const a = document.createElement("a");
    a.href = canvasRef.current.toDataURL("image/png");
    a.download = `pulse-daily-${result.date}.png`;
    a.click();
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareText(result));
      setCopied(true);
      toast.success("Results copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Clipboard unavailable — select and copy the text below");
    }
  };

  return (
    <div data-testid="score-card">
      <canvas ref={canvasRef} width={W} height={H} className="w-full border border-phosphor/40" data-testid="score-card-canvas" />
      <div className="mt-4 flex flex-wrap gap-3">
        <button onClick={download} data-testid="score-card-download" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 text-xs tracking-widest font-semibold hover:bg-primary/90">
          <Download className="w-3.5 h-3.5" /> DOWNLOAD PNG
        </button>
        <button onClick={copy} data-testid="score-card-copy" className="inline-flex items-center gap-2 border border-phosphor/50 px-5 py-3 text-xs tracking-widest hover:bg-primary/10">
          {copied ? <Check className="w-3.5 h-3.5 text-phosphor" /> : <Copy className="w-3.5 h-3.5" />} COPY RESULTS TEXT
        </button>
      </div>
      <pre className="mt-4 text-xs text-muted-foreground whitespace-pre-wrap border border-border p-3" data-testid="score-card-text">{shareText(result)}</pre>
    </div>
  );
}
